import React, { useState } from "react";
import {
    Accordion,
    Grid,
    Header,
    Dropdown,
    Radio,
    Icon,
    Segment,
    Input,
    Label,
} from "semantic-ui-react";
import "./index.scss";
import { Controller } from "react-hook-form";
import { PERMISSION_ORDER, SETTINGS_ORDER, sortByOrder } from "./helper";

const actionKeys = ["view", "add", "edit", "delete"];

const getNested = (obj, path) => {
    return path?.reduce?.(
        (acc, key) => (acc && acc[key] ? acc[key] : null),
        obj
    );
};

const getTitle = (value) => {
    if (!value) return "";

    const lower = value?.toLowerCase?.();

    if (lower === "crm_customization") {
        return "CRM Customization";
    }

    if (lower === "texting/tcr") {
        return "Texting/TCR";
    }

    return value?.replace?.(/_/g, " ");
};

const PermissionSection = ({
    title,
    color,
    index,
    isActive,
    onClick,
    isDisabled,
    isChecked,
    onToggle,
    children,
    readOnly,
    description,
}) => (
    <Accordion fluid styled exclusive={false} className="PermissionAccordion">
        <Accordion.Title
            active={isActive}
            index={index}
            onClick={onClick}
            className={`permission-title bg-${color}`}
        >
            <Icon name="dropdown" color="blue" />
            <Header as="h4" className="capitalize">
                {title}
            </Header>
            <Radio
                toggle
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                disabled={isDisabled}
                checked={isChecked}
                onChange={onToggle}
                readOnly={readOnly}
            />
            <br />
            <small>{description}</small>
        </Accordion.Title>
        <Accordion.Content active={isActive}>{children}</Accordion.Content>
    </Accordion>
);

const PermissionMatrix = ({
    permissions,
    control,
    trigger,
    onChange,
    disabled,
    readOnly,
    name = "permission",
}) => {
    const [expandAll, setExpandAll] = useState(false);
    const [activeIndexes, setActiveIndexes] = useState([]);

    const orderedPermissions = sortByOrder(permissions, PERMISSION_ORDER);

    const handleAccordionClick = (_, { index }) => {
        const newIndexes = activeIndexes.includes(index)
            ? activeIndexes.filter((i) => i !== index)
            : [...activeIndexes, index];
        setActiveIndexes(newIndexes);
    };

    const handleAccessChange = async (path, action, newAccess) => {
        if (disabled || readOnly) return;
        const updated = structuredClone(permissions);

        // path example: ['settings', 'general_settings', 'users']
        const fullPath = [...path, action];
        const target = getNested(updated, fullPath);
        if (!target) return;

        // Core update
        target.access_control = newAccess;

        // Cyclical dependencies
        const parentNode = getNested(updated, path);
        if (!parentNode) return;

        if (action === "view" && newAccess === "off") {
            ["add", "edit", "delete"].forEach((key) => {
                if (parentNode[key]) {
                    parentNode[key].access_control = "off";
                    parentNode[key].attribute = [];
                }
            });
        }
        await trigger(`${name}.${path.join(".")}.${action}`);
        onChange?.(updated);
    };

    const handleAttributeChange = async (path, action, newAttributes) => {
        if (disabled || !permissions || readOnly) return;

        const updated = structuredClone(permissions);
        const target = getNested(updated, [...path, action]);
        if (target) target.attribute = newAttributes;

        onChange?.(updated);
    };

    const isAllOff = (obj) => {
        return Object.values(obj).every((val) => {
            if (val && typeof val === "object" && !("access_control" in val)) {
                return isAllOff(val);
            }
            if (typeof val === "string") return true;
            return val.access_control === "off";
        });
    };

    const isOffRestricted = (obj) => {
        return Object.values(obj).some((val) => {
            if (!val || typeof val !== "object") return false;

            if (Array.isArray(val.options_access_control)) {
                return !val.options_access_control.includes("off");
            }

            return isOffRestricted(val);
        });
    };

    const setAllAccessToOffDeep = (obj) => {
        const newObj = {};
        Object.entries(obj).forEach(([key, val]) => {
            if (val && typeof val === "object") {
                if ("access_control" in val) {
                    newObj[key] = {
                        ...val,
                        access_control: "off",
                        attribute: [],
                    };
                } else {
                    newObj[key] = setAllAccessToOffDeep(val);
                }
            } else {
                newObj[key] = val;
            }
        });
        return newObj;
    };

    const handleToggleSection = (sectionKey) => {
        if (disabled || readOnly) return;
        const updated = structuredClone(permissions);
        const section = updated[sectionKey];

        if (!section) return;

        const allOff = isAllOff(section);
        if (!allOff) {
            updated[sectionKey] = setAllAccessToOffDeep(section);
        }

        onChange?.(updated);
    };

    const handleToggleSubSection = (sectionKey, subKey) => {
        if (disabled || readOnly) return;
        const updated = structuredClone(permissions);
        const subSection = updated[sectionKey][subKey];

        if (!subSection) return;

        const allOff = isAllOff(subSection);
        if (!allOff) {
            updated[sectionKey][subKey] = setAllAccessToOffDeep(subSection);
        }

        onChange?.(updated);
    };

    const handleToggleAllSections = () => {
        setExpandAll((prev) => {
            if (prev) {
                setActiveIndexes([]);
            } else {
                setActiveIndexes(() => {
                    const indexes = Object.keys(orderedPermissions)?.map?.(
                        (section, sectionIndex) => {
                            if (section === "settings") {
                                return Object.keys(orderedPermissions[section])
                                    ?.map?.((_, i) => `${sectionIndex}-${i}`)
                                    .concat(sectionIndex);
                            }
                            return sectionIndex;
                        }
                    );
                    return indexes.flat();
                });
            }
            return !prev;
        });
    };

    const renderActionColumns = (path, actions, showHeader, options) => (
        <Grid columns={5}>
            <Grid.Row>
                <Grid.Column>{actions?.description}</Grid.Column>
                {actionKeys.map((action) => {
                    const actionObj = actions?.[action];
                    const restrictedOptions =
                        options?.[action]?.options_attribute?.map?.((item) => ({
                            key: item,
                            text: item?.replace?.(
                                /(?:^|\s|_)(\w)/g,
                                (match, p1) =>
                                    p1.toUpperCase().replace("_", " ")
                            ),
                            value: item,
                        })) ?? [];

                    const accessOptions =
                        options?.[action]?.options_access_control?.map?.(
                            (item) => ({
                                key: item,
                                text: item
                                    ?.charAt?.(0)
                                    .toUpperCase?.()
                                    .concat(
                                        item?.slice?.(1)?.replace?.(/_/g, " ")
                                    ),
                                value: item,
                            })
                        ) ?? [];

                    return (
                        <Grid.Column key={action}>
                            {showHeader && (
                                <Header as="h5" className="capitalize">
                                    {action}
                                </Header>
                            )}
                            <div className="DropdownWrapper">
                                {actionObj?.access_control && (
                                    <Controller
                                        name={`${name}.${path.join(
                                            "."
                                        )}.${action}.access_control`}
                                        control={control}
                                        render={({ field }) => {
                                            if (readOnly) {
                                                return (
                                                    <Input
                                                        {...field}
                                                        className="capitalize"
                                                        readOnly
                                                    />
                                                );
                                            }
                                            return (
                                                <Dropdown
                                                    {...field}
                                                    value={field.value}
                                                    fluid
                                                    selection
                                                    options={accessOptions}
                                                    onChange={(
                                                        _,
                                                        { value }
                                                    ) => {
                                                        field.onChange(value);
                                                        handleAccessChange(
                                                            path,
                                                            action,
                                                            value
                                                        );
                                                    }}
                                                />
                                            );
                                        }}
                                    />
                                )}

                                {actionObj?.access_control === "restricted" && (
                                    <Controller
                                        name={`${name}.${path.join(
                                            "."
                                        )}.${action}.attribute`}
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error },
                                        }) => (
                                            <>
                                                <Dropdown
                                                    {...field}
                                                    value={field.value}
                                                    fluid
                                                    multiple
                                                    selection
                                                    search
                                                    placeholder="Select attribute..."
                                                    error={Boolean(
                                                        error?.message
                                                    )}
                                                    options={restrictedOptions}
                                                    style={{
                                                        pointerEvents: readOnly
                                                            ? "none"
                                                            : "auto",
                                                    }}
                                                    onChange={(
                                                        _,
                                                        { value }
                                                    ) => {
                                                        field.onChange(value);
                                                        handleAttributeChange(
                                                            path,
                                                            action,
                                                            value
                                                        );
                                                    }}
                                                />
                                                {error?.message && (
                                                    <Label basic>
                                                        {error?.message}
                                                    </Label>
                                                )}
                                            </>
                                        )}
                                    />
                                )}
                            </div>
                        </Grid.Column>
                    );
                })}
            </Grid.Row>
        </Grid>
    );

    const renderSettingsLayout = (section, sectionIndex) => (
        <PermissionSection
            color="primary"
            index={sectionIndex}
            isActive={activeIndexes?.includes?.(sectionIndex)}
            isChecked={!isAllOff(permissions?.[section])}
            isDisabled={
                disabled ||
                isAllOff(permissions?.[section]) ||
                isOffRestricted(permissions?.[section])
            }
            onClick={handleAccordionClick}
            onToggle={() => handleToggleSection(section)}
            title={"Settings"}
            key={section}
        >
            {Object.keys(
                sortByOrder(permissions?.[section], Object.keys(SETTINGS_ORDER))
            )?.map?.((nestedKey, i) => {
                const sectionData = permissions?.[section]?.[nestedKey];
                const isSectionDataObject =
                    typeof sectionData === "object" && sectionData !== null;
                const hasDirectActions = actionKeys?.some?.(
                    (k) => isSectionDataObject && k in sectionData
                );
                const orderedNestedKeys = SETTINGS_ORDER[nestedKey]
                    ? SETTINGS_ORDER[nestedKey]
                    : Object.keys(permissions?.[section]?.[nestedKey]);
                return (
                    <PermissionSection
                        color="secondary"
                        index={`${sectionIndex}-${i}`}
                        isActive={activeIndexes.includes(
                            `${sectionIndex}-${i}`
                        )}
                        isDisabled={
                            disabled ||
                            isAllOff(permissions?.[section]?.[nestedKey]) ||
                            isOffRestricted(permissions?.[section]?.[nestedKey])
                        }
                        isChecked={
                            !isAllOff(permissions?.[section]?.[nestedKey])
                        }
                        onClick={handleAccordionClick}
                        onToggle={() =>
                            handleToggleSubSection(section, nestedKey)
                        }
                        title={getTitle(nestedKey)}
                        key={nestedKey}
                        description={
                            permissions?.[section]?.[nestedKey]?.description
                        }
                    >
                        {hasDirectActions ? (
                            <div className="permission-group">
                                {renderActionColumns(
                                    [section, nestedKey],
                                    sectionData,
                                    true,
                                    permissions?.[section]?.[nestedKey]
                                )}
                            </div>
                        ) : (
                            orderedNestedKeys?.map?.((subKey, subIndex) => (
                                <div
                                    key={subKey}
                                    className="permission-group nested"
                                >
                                    <div className="permission-title">
                                        {subKey !== "description" && (
                                            <Header
                                                as="h5"
                                                className="capitalize"
                                            >
                                                {subKey.replace(/_/g, " ")}
                                            </Header>
                                        )}
                                    </div>
                                    {renderActionColumns(
                                        [section, nestedKey, subKey],
                                        permissions?.[section]?.[nestedKey]?.[
                                            subKey
                                        ],
                                        subIndex === 0,
                                        permissions?.[section]?.[nestedKey]?.[
                                            subKey
                                        ]
                                    )}
                                </div>
                            ))
                        )}
                    </PermissionSection>
                );
            })}
        </PermissionSection>
    );

    // Standard layout
    const renderStandardLayout = (section, sectionIndex) => {
        const sectionData = permissions?.[section];
        const isSectionDataObject =
            typeof sectionData === "object" && sectionData !== null;
        const hasDirectActions = actionKeys?.some?.(
            (k) => isSectionDataObject && k in sectionData
        );

        return (
            <PermissionSection
                color="primary"
                index={sectionIndex}
                isActive={activeIndexes.includes(sectionIndex)}
                isDisabled={
                    disabled ||
                    isAllOff(permissions[section]) ||
                    isOffRestricted(permissions[section])
                }
                isChecked={!isAllOff(permissions[section])}
                onClick={handleAccordionClick}
                onToggle={() => handleToggleSection(section)}
                title={getTitle(section)}
                key={section}
            >
                {hasDirectActions ? (
                    <div className="permission-group">
                        {renderActionColumns(
                            [section],
                            sectionData,
                            true,
                            permissions[section]
                        )}
                    </div>
                ) : (
                    Object.keys(permissions[section]).map((subKey, index) => (
                        <div key={subKey} className="permission-group">
                            <div className="permission-title">
                                <Header as="h5" className="capitalize">
                                    {subKey.replace(/_/g, " ")}
                                </Header>
                            </div>
                            {renderActionColumns(
                                [section, subKey],
                                permissions[section][subKey],
                                index === 0,
                                permissions[section][subKey]
                            )}
                        </div>
                    ))
                )}
            </PermissionSection>
        );
    };

    if (!permissions)
        return (
            <Segment>
                <Header as="h4" content="No Permissions were found " />
            </Segment>
        );

    return (
        <div className="PermissionMatrix">
            <div
                className="permission-title expand-all"
                onClick={handleToggleAllSections}
            >
                <Icon
                    name="dropdown"
                    color="blue"
                    rotated={expandAll ? "" : "counterclockwise"}
                />
                <Header className="capitalize" as="h3">
                    {expandAll ? "Collapse" : "Expand"} All
                </Header>
            </div>
            {Object.keys(orderedPermissions).map((section, sectionIndex) =>
                section === "settings"
                    ? renderSettingsLayout(section, sectionIndex)
                    : renderStandardLayout(section, sectionIndex)
            )}
        </div>
    );
};

export default PermissionMatrix;
